import { type ReactElement } from "react";
import {
  Legend,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from "recharts";
import { cn } from "../../lib/utils.js";
import { useReducedMotion } from "../../hooks/useReducedMotion.js";
import {
  ChartTooltip,
  type ChartTooltipEntry,
} from "../../molecules/ChartTooltip/index.js";

export interface MaturityRadarAxis {
  /** Axis label — e.g. a maturity pillar name. */
  readonly label: string;
  /**
   * Current score on the `[0, max]` scale, or `null` for an axis with NO score.
   * A null axis keeps its label and leaves a GAP in the shape rather than a
   * point at zero — an unassessed pillar is missing, never a zero.
   */
  readonly value: number | null;
  /** Optional target score; when present on any axis, a dashed overlay renders. */
  readonly target?: number;
  /**
   * Optional prior-period score; when present on any axis, a dotted overlay renders
   * so movement reads as the space between the two shapes. Domain-neutral — the
   * consumer names the period via `previousLabel`. `null` leaves a gap, as above.
   */
  readonly previous?: number | null;
}

export interface MaturityRadarProps {
  /** One entry per axis; a profile needs at least three axes. */
  readonly data: readonly MaturityRadarAxis[];
  /** Scale maximum (default 5 — the LAMA 1–5 maturity scale). */
  readonly max?: number;
  /**
   * Unit suffix for every number the chart prints — the radius ticks and the
   * tooltip. `"%"` for the percent scale; empty for a bare ordinal one.
   */
  readonly unit?: string;
  /** Legend label for the current-shape series. */
  readonly currentLabel?: string;
  /** Legend label for the target-overlay series. */
  readonly targetLabel?: string;
  /** Legend label for the prior-period overlay series. */
  readonly previousLabel?: string;
  /** Accessible summary; defaults to a generated description. */
  readonly ariaLabel?: string;
  /** Fixed chart height in px (the width is fluid). */
  readonly height?: number;
  readonly className?: string;
}

/**
 * A TICK COUNT IS A GRID DENSITY, and that is the whole of this function.
 *
 * recharts derives `PolarGrid`'s concentric rings from the radius axis's own
 * ticks — `generateCategoricalChart` clones the grid with
 * `polarRadius = getTicksOfAxis(radiusAxis, true)` — so one number decides both
 * how many rings are drawn and how many labels stack along the radius spoke.
 * There is no separate `gridCount`.
 *
 * The shipped value was `max + 1`, which is right for the 1–5 maturity scale it
 * was written against and catastrophic for the percent scale the LAMA report
 * moved to (ADR-0027's 2026-09-10 amendment): 101 rings and 101 overlapping
 * labels, i.e. a solid grey disc with an unreadable column of numbers beside
 * it. Above a small ordinal scale the axis therefore takes five deliberate
 * rings instead, which `[0, 100]` resolves to 0 / 25 / 50 / 75 / 100.
 */
const WHOLE_STEP_SCALE_MAX = 10;
const DELIBERATE_RING_COUNT = 5;

export function maturityRadarTickCount(max: number): number {
  return max <= WHOLE_STEP_SCALE_MAX ? max + 1 : DELIBERATE_RING_COUNT;
}

/**
 * Where the radius spoke — the one carrying the ring numbers — is parked.
 *
 * The angle axes start at 90° and step by `360 / n`, so half a step off the
 * first one always lands BETWEEN two of them. recharts' default of `0°` sits
 * on a data axis whenever four divides the axis count, which at twelve pillars
 * means the ring's own numbers are printed along a pillar's line.
 */
export function maturityRadarRadiusAngle(axisCount: number): number {
  return 90 - 180 / Math.max(axisCount, 1);
}

/** Characters a wrapped axis label fits on one line at 11px. */
const AXIS_LABEL_LINE_CHARS = 18;
const AXIS_LABEL_MAX_LINES = 2;
const AXIS_LABEL_LINE_HEIGHT = 12;

/**
 * Wrap an axis label onto at most two lines, ellipsising whatever will not fit.
 *
 * Character-based and pure ON PURPOSE. recharts' own `<Text width>` measures
 * through the DOM, which returns zero under jsdom and varies with the font the
 * VR container happens to resolve — so the wrap the tests see would not be the
 * wrap that ships. A twelve-pillar profile is exactly the case that needs this:
 * unwrapped labels 30° apart collide near the top and bottom of the ring and
 * run off the canvas at 3 and 9 o'clock.
 */
export function wrapAxisLabel(
  label: string,
  lineChars: number = AXIS_LABEL_LINE_CHARS,
  maxLines: number = AXIS_LABEL_MAX_LINES,
): readonly string[] {
  const words = label.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];
  const lines: string[] = [];
  let current = words[0]!;
  for (const word of words.slice(1)) {
    const candidate = `${current} ${word}`;
    if (candidate.length <= lineChars) {
      current = candidate;
    } else if (lines.length + 1 < maxLines) {
      lines.push(current);
      current = word;
    } else {
      // The last line is full but words remain: keep packing so the ellipsis
      // below reports the overflow, rather than silently dropping the tail.
      current = candidate;
    }
  }
  lines.push(current);
  const lastIndex = lines.length - 1;
  return lines.map((line, index) =>
    index === lastIndex && line.length > lineChars
      ? `${line.slice(0, lineChars - 1)}…`
      : line,
  );
}

/** The subset of recharts' tick props this renderer reads. */
interface RadarAxisTickProps {
  readonly x?: number;
  readonly y?: number;
  readonly textAnchor?: "start" | "middle" | "end" | "inherit";
  readonly payload?: { readonly value?: string | number };
}

/**
 * A polar angle-axis label. Unlike the cartesian precedent in `paretoL1` it
 * must honour the injected `textAnchor` — on a ring the anchor flips per
 * quadrant, so a hard-coded `"middle"` pushes the 3 and 9 o'clock labels
 * through the shape. `<title>` keeps the full wording reachable on hover when
 * the visible text was ellipsised.
 */
function RadarAxisTick({
  x = 0,
  y = 0,
  textAnchor = "middle",
  payload,
}: RadarAxisTickProps): ReactElement {
  const label = String(payload?.value ?? "");
  const lines = wrapAxisLabel(label);
  // Centre the block on the tick point, so a two-line label reads the same at
  // the top of the ring as at the bottom.
  const top = y - ((lines.length - 1) * AXIS_LABEL_LINE_HEIGHT) / 2;
  return (
    <text
      x={x}
      y={top}
      textAnchor={textAnchor}
      dominantBaseline="central"
      fontSize={11}
      fill="hsl(var(--pb-text-muted))"
      className="recharts-polar-angle-axis-tick-value"
    >
      {/* Kept hand-built: an SVG <title> is the only hover hint an axis label
          (a chart mark, not a focusable control) can carry. */}
      <title>{label}</title>
      {lines.map((line, index) => (
        <tspan
          key={`${String(index)}-${line}`}
          x={x}
          dy={index === 0 ? 0 : AXIS_LABEL_LINE_HEIGHT}
        >
          {line}
        </tspan>
      ))}
    </text>
  );
}

export interface RadarTooltipProps {
  /** recharts clones this element with its own tooltip props. */
  readonly active?: boolean | undefined;
  readonly label?: string | number | undefined;
  readonly payload?: readonly ChartTooltipEntry[] | undefined;
  readonly unit?: string;
  /** The chart's scale maximum — decides how precisely a reading is printed. */
  readonly max?: number;
}

/**
 * A READING, unlike a grid ring, is not always whole. On the percent scale a
 * whole number is the product rule (ADR-0027 D8); on a small ordinal scale it
 * is a lie — rounding 3.6 to "4" moves a score by a fifth of the whole range,
 * which is wider than the gap between two maturity levels.
 */
function formatReading(value: number, unit: string, max: number): string {
  return max > WHOLE_STEP_SCALE_MAX
    ? `${Math.round(value)}${unit}`
    : `${String(Number(value.toFixed(2)))}${unit}`;
}

/**
 * The radar's hover readout: the axis name, then one row per series with its
 * exact rounded value.
 *
 * IT IS A CUSTOM `content` RATHER THAN A `formatter`, and that is forced rather
 * than preferred. `DefaultTooltipContent` calls a formatter only when
 * `finalValue != null`, so an UNASSESSED axis — the whole reason this chart
 * draws gaps — would render as a series name, a separator and nothing at all.
 * Saying "Not assessed" is the same rule the gapped shape encodes visually, and
 * a formatter cannot say it. Owning the markup also settles two colours
 * recharts hardcodes past `contentStyle`: the value row (`entry.color`) and the
 * panel background (`#fff`).
 *
 * The markup is the shared `ChartTooltip`, which this module-internal wrapper
 * feeds the radar's reading rule and its "Not assessed" wording; the DOM is
 * unchanged from when the radar drew it itself.
 *
 * Decorative by construction — it renders inside the chart's `aria-hidden`
 * subtree, so the accessible reading is the consumer's table, not this.
 */
export function RadarTooltip({
  active,
  label,
  payload,
  unit = "",
  max = 5,
}: RadarTooltipProps): ReactElement | null {
  return (
    <ChartTooltip
      data-testid="maturity-radar-tooltip"
      active={active}
      label={label}
      payload={payload}
      missingLabel="Not assessed"
      formatValue={(value) =>
        typeof value === "number"
          ? formatReading(value, unit, max)
          : "Not assessed"
      }
    />
  );
}

/**
 * Maturity radar — a pillar profile plotted on a fixed `[0, max]` scale, with an
 * optional dashed target overlay so the gap-to-target reads as the space between
 * the two shapes (SAFe Measure-and-Grow / AgilityHealth idiom). Fully data-blind:
 * scores and targets arrive as props; no clock, no fetch. A standalone organism
 * like `XMatrixEditor` (no `def.ts`, no `registerWidget`), reusable by
 * any maturity module.
 *
 * The tooltip is a POINTER ENHANCEMENT and nothing more. recharts renders it
 * inside the `aria-hidden` chart subtree with `tabIndex={-1}` and no role, and
 * its `accessibilityLayer` bails out on a centric layout, so it can never be
 * reached by keyboard. The non-pointer equivalent is the consumer's own table
 * of the same rows — which is why `ariaLabel` names the unscored axes and why
 * the LAMA hierarchy section renders every figure again underneath.
 */
export function MaturityRadar({
  data,
  max = 5,
  unit = "",
  currentLabel = "Current",
  targetLabel = "Target",
  previousLabel = "Previous",
  ariaLabel,
  height = 320,
  className,
}: MaturityRadarProps): ReactElement {
  const reducedMotion = useReducedMotion();
  const hasTarget = data.some((d) => typeof d.target === "number");
  const hasPrevious = data.some((d) => typeof d.previous === "number");
  const chartData = data.map((d) => ({
    label: d.label,
    value: d.value,
    target: d.target ?? null,
    previous: d.previous ?? null,
  }));
  // Compose the overlay clauses rather than concatenating each one: naive
  // concatenation comma-splices when both are present.
  const overlays = [
    hasTarget ? "a target overlay" : null,
    hasPrevious ? "a previous overlay" : null,
  ].filter((clause): clause is string => clause !== null);
  const summary =
    ariaLabel ??
    `Maturity profile across ${data.length} pillars on a ${max}-point scale${
      overlays.length > 0 ? `, with ${overlays.join(" and ")}` : ""
    }.`;
  /* The RING numbers are whole on every scale — they are the grid, and the
     grid is chosen (0/25/50/75/100, or 0..5). */
  const formatTick = (value: number): string => `${Math.round(value)}${unit}`;
  /* Horizontal room for the axis labels, scaled to the HEIGHT rather than
     fixed. The polar radius is `min(width, height) / 2` taken after the margin
     AND after the legend's own box, so on a card wider than it is tall this
     spends width the ring could never have used — but a fixed 72px would start
     binding on a narrow consumer and shrink the very chart it is padding. */
  const sideMargin = Math.round(height * 0.15);

  return (
    <div
      role="img"
      aria-label={summary}
      className={cn("w-full", className)}
      style={{ height }}
    >
      <div aria-hidden="true" className="h-full w-full">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart
            data={chartData}
            outerRadius="76%"
            margin={{
              top: 8,
              right: sideMargin,
              bottom: 8,
              left: sideMargin,
            }}
          >
            <PolarGrid stroke="hsl(var(--pb-border))" />
            <PolarAngleAxis dataKey="label" tick={RadarAxisTick} />
            <PolarRadiusAxis
              domain={[0, max]}
              tickCount={maturityRadarTickCount(max)}
              tickFormatter={formatTick}
              angle={maturityRadarRadiusAngle(data.length)}
              tick={{ fontSize: 10, fill: "hsl(var(--pb-text-muted))" }}
              axisLine={false}
            />
            {/* previous → target → current: recharts paints later siblings on
                top, so Current stays dominant and the legend reads
                quietest-to-loudest.

                `connectNulls={false}` is recharts' own default and is spelled
                out on the two series that can carry a null so the rule is
                visible where it matters: a null axis is a gap in the shape,
                never a line drawn across it. `target` omits it because a
                target is either present on an axis or absent from the series.

                A GAPPED SHAPE LOSES ITS FILL, and that is recharts, not a
                choice: `Polygon` only fills a path it closed, so one
                unassessed pillar drops `fillOpacity` for the whole series.
                The dots below are what keeps an assessed axis legible when
                that happens — they mark real readings without drawing
                anything across the gap. */}
            {hasPrevious ? (
              <Radar
                name={previousLabel}
                dataKey="previous"
                stroke="hsl(var(--pb-text-muted))"
                strokeDasharray="2 3"
                strokeWidth={1.5}
                fill="transparent"
                connectNulls={false}
                isAnimationActive={!reducedMotion}
              />
            ) : null}
            {hasTarget ? (
              <Radar
                name={targetLabel}
                dataKey="target"
                stroke="hsl(var(--pb-teal))"
                strokeDasharray="4 3"
                strokeWidth={1.5}
                fill="transparent"
                isAnimationActive={!reducedMotion}
              />
            ) : null}
            <Radar
              name={currentLabel}
              dataKey="value"
              stroke="hsl(var(--pb-cyan))"
              strokeWidth={2}
              fill="hsl(var(--pb-cyan))"
              fillOpacity={0.18}
              dot={{
                r: 2.5,
                fill: "hsl(var(--pb-cyan))",
                stroke: "hsl(var(--pb-card))",
                strokeWidth: 1,
              }}
              connectNulls={false}
              isAnimationActive={!reducedMotion}
            />
            {/* `cursor={false}`: a polar cursor is a radial sector, not the rect
                fill the cartesian charts pass, and drawing one over an already
                dense ring adds noise rather than aim. `content` rather than
                `formatter` — see `RadarTooltip`. */}
            <RechartsTooltip
              cursor={false}
              /* `filterNull` DEFAULTS TO TRUE, and that default is the opposite
                 of what this chart needs: it strips every null-valued series
                 out of the payload before `content` is called, so an
                 unassessed axis would arrive as a shorter list and the reading
                 would silently become "this pillar has one series" rather than
                 "this pillar is not assessed". */
              filterNull={false}
              content={<RadarTooltip unit={unit} max={max} />}
            />
            <Legend
              wrapperStyle={{ fontSize: 12, paddingTop: 4 }}
              iconType="plainline"
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
